import { noop } from 'es-toolkit/compat'
import useOnClickOverlay from 'hooks/click-overlay'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
} from '@mui/material'
import { Close } from '@farmtrx/farmtrx-icons'
import { useTranslation } from 'react-i18next'

import Draggable from 'react-draggable'
import { TEST_IDS } from 'test-ids'
import { ModalHeaderRegistryContext } from './modal-header'

function PaperComponent({ ...paperProps }) {
  const nodeRef = useRef(null)
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...paperProps} ref={nodeRef} />
    </Draggable>
  )
}

export function getDialogTitleProps({ title, draggable }) {
  const hasTitle = Boolean(title)

  if (!hasTitle) {
    return {
      titleId: undefined,
      titleElement: null,
    }
  }

  return {
    titleId: TEST_IDS.modalDialogTitle,
    titleElement: (
      <DialogTitle
        id={TEST_IDS.modalDialogTitle}
        data-testid={TEST_IDS.modalDialogTitle}
        sx={{
          cursor: draggable ? 'move' : 'default',
          marginLeft: draggable ? '8px' : 0,
          padding: draggable ? 0 : undefined,
        }}
      >
        {title}
      </DialogTitle>
    ),
  }
}

export default function ModalInternal(props) {
  const {
    title,
    onClose = noop,
    children,
    draggable = false,
    maxWidth = 'sm',
    fullWidth = false,
  } = props

  const { t } = useTranslation()
  const ref = useRef()

  const onCloseClicked = useCallback(() => {
    onClose()
  }, [onClose])

  useOnClickOverlay(ref, () => {
    onClose()
  })

  const { titleId, titleElement } = getDialogTitleProps({ title, draggable })

  // The Dialog's accessible name can come from two mutually exclusive
  // sources: the `title` prop (rendered as a DialogTitle right here) or a
  // ModalHeader somewhere in `children` registering itself. aria-labelledby
  // only ever points at TEST_IDS.modalDialogTitle once one of those actually
  // exists in the DOM — otherwise it's left unset rather than dangling.
  const [registeredHeaderCount, setRegisteredHeaderCount] = useState(0)
  const registerHeader = useCallback((delta) => {
    setRegisteredHeaderCount((count) => count + delta)
  }, [])
  const hasAccessibleTitle = Boolean(titleId) || registeredHeaderCount > 0

  return (
    <Dialog
      open={true}
      onClose={onCloseClicked}
      aria-labelledby={
        hasAccessibleTitle ? TEST_IDS.modalDialogTitle : undefined
      }
      data-testid={TEST_IDS.modalDialog}
      PaperComponent={draggable ? PaperComponent : Paper}
      hideBackdrop={draggable ? true : false}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      {titleElement}
      <IconButton
        aria-label={t('close')}
        onClick={onCloseClicked}
        data-testid={TEST_IDS.modalClose}
        size="small"
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <Close />
      </IconButton>
      <DialogContent data-testid={TEST_IDS.modalDialogContent}>
        <ModalHeaderRegistryContext.Provider value={registerHeader}>
          {children}
        </ModalHeaderRegistryContext.Provider>
      </DialogContent>
    </Dialog>
  )
}

ModalInternal.propTypes = {
  onClose: PropTypes.func,
  title: PropTypes.string,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  fullWidth: PropTypes.bool,
}
